'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Icon } from '@iconify/react';
import DragHandleOutlinedIcon from '@mui/icons-material/DragHandleOutlined';
import { Box, ClickAwayListener, IconButton, ListItemIcon, ListItemText, Menu, MenuItem as MuiMenuItem, MenuList, Paper, Popper, Tooltip, Typography, useTheme, alpha } from '@mui/material';
import { HFilledTextField } from '@ui/components/common/TextField';
import { Buildings, SearchNormal1, Task, TextBlock, TickSquare, ToggleOnCircle } from 'iconsax-react';
import { useMemo, useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useResponsive } from '@ui/style/useResponsive';
import { FieldType } from '@application/enums/field-types';
import { ADDITIONAL_ATTENDEES_TYPE } from '../../presets/additionalAttendeesPreset';
const FormFieldsMenu = ({ onSelect, selectedItemId, anchorPosition = 'right', anchorRef, onClose, formFields = [], restrictToTypes, disableUniquenessChecks }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { isMobile } = useResponsive();
    const [submenuAnchor, setSubmenuAnchor] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
    const [hoveredItem, setHoveredItem] = useState(null);
    const [clickedItem, setClickedItem] = useState(null);
    const submenuTimeoutRef = useRef();
    const scrollContainerRef = useRef(null);
    const stickyHeaderRef = useRef(null);
    const open = Boolean(anchorRef);
    const getAddedOnceTooltip = useCallback((fieldName) => t('events.formFieldsMenu.fieldAlreadyAddedCannotAddAgain', { fieldName }), [t]);
    // Helper function to normalize fieldType for comparison
    const normalizeFieldType = (fieldType) => {
        if (typeof fieldType === 'number') {
            return String(fieldType);
        }
        if (typeof fieldType === 'string') {
            // If it's already a numeric string, return as is
            if (!isNaN(parseInt(fieldType))) {
                return fieldType;
            }
            // Map string types to enum values
            const stringToEnum = {
                text: FieldType.TEXT,
                email: FieldType.EMAIL,
                phone: FieldType.PHONE,
                date: FieldType.DATE,
                time: FieldType.TIME,
                multiline: FieldType.MULTILINE,
                radio: FieldType.RADIO,
                choice: FieldType.CHOICE,
                'checkbox-group': FieldType.CHECKBOX_GROUP,
                dropdown: FieldType.DROPDOWN,
                companyDropdown: FieldType.COMPANYDROPDOWN,
                'multi-select': FieldType.MULTI_SELECT,
                toggle: FieldType.TOGGLE,
                undertaking: FieldType.UNDERTAKING,
                pickupLocation: FieldType.PICKUPLOCATION
            };
            return stringToEnum[fieldType] || fieldType;
        }
        return String(fieldType);
    };
    // These "only once per event" rules are about the MAIN form's fields (Email/Phone are tied to the
    // CRM-matching flow, Company Dropdown/Pickup Location/Undertaking/Additional Attendees are each
    // singletons at the event level) - when this menu is opened from INSIDE an Additional Attendees
    // group (disableUniquenessChecks=true), a sub-question with the same type isn't the same field and
    // shouldn't be blocked by what exists elsewhere on the main form.
    const hasUndertakingField = !disableUniquenessChecks &&
        formFields.some((field) => normalizeFieldType(field.fieldType) === FieldType.UNDERTAKING);
    // Check if there's already an email field
    const hasEmailField = !disableUniquenessChecks &&
        formFields.some((field) => normalizeFieldType(field.fieldType) === FieldType.EMAIL);
    // Check if there's already a phone field
    const hasPhoneField = !disableUniquenessChecks &&
        formFields.some((field) => normalizeFieldType(field.fieldType) === FieldType.PHONE);
    // Check if there's already a company dropdown field
    const hasCompanyDropdownField = !disableUniquenessChecks &&
        formFields.some((field) => normalizeFieldType(field.fieldType) === FieldType.COMPANYDROPDOWN);
    // Check if there's already a pickup location field
    const hasPickupLocationField = !disableUniquenessChecks &&
        formFields.some((field) => normalizeFieldType(field.fieldType) === FieldType.PICKUPLOCATION);
    //Check if the pickupLocation is not linked with the Transportation Preset
    const hasStandalonePickupLocationField = !disableUniquenessChecks &&
        formFields.some((field) => normalizeFieldType(field.fieldType) === FieldType.PICKUPLOCATION &&
            !String(field?.fieldAdditionalNote || '').startsWith('preset.transport.'));
    // Check if transportation preset has already been added (any preset field exists).
    const hasTransportationPresetField = !disableUniquenessChecks &&
        formFields.some((field) => String(field?.fieldAdditionalNote || '').startsWith('preset.transport.'));
    // Example data (label kept for onSelect callback; labelKey used for display/translation)
    const menuSchema = useMemo(() => [
        {
            id: 'singleLineInput',
            label: t('events.formFieldsMenu.singleLineInput'),
            labelKey: 'events.formFieldsMenu.singleLineInput',
            description: t('events.formFieldsMenu.singleLineInputDescription'),
            icon: _jsx(Icon, { icon: 'eva:text-fill', fontSize: '20', color: theme.palette.common.black }),
            children: [
                {
                    id: 'text',
                    label: t('events.formFieldsMenu.text'),
                    labelKey: 'events.formFieldsMenu.text',
                    type: 'text'
                },
                {
                    id: 'email',
                    label: t('events.formFieldsMenu.email'),
                    labelKey: 'events.formFieldsMenu.email',
                    type: 'email',
                    disabled: hasEmailField,
                    disabledTooltip: hasEmailField
                        ? getAddedOnceTooltip(t('events.formFieldsMenu.email'))
                        : undefined
                },
                {
                    id: 'phone',
                    label: t('events.formFieldsMenu.phone'),
                    labelKey: 'events.formFieldsMenu.phone',
                    type: 'phone',
                    disabled: hasPhoneField,
                    disabledTooltip: hasPhoneField
                        ? getAddedOnceTooltip(t('events.formFieldsMenu.phone'))
                        : undefined
                },
                {
                    id: 'date',
                    label: t('events.formFieldsMenu.date'),
                    labelKey: 'events.formFieldsMenu.date',
                    type: 'date'
                },
                {
                    id: 'time',
                    label: t('events.formFieldsMenu.time'),
                    labelKey: 'events.formFieldsMenu.time',
                    type: 'time'
                }
            ]
        },
        {
            id: 'longText',
            label: t('events.formFieldsMenu.longText'),
            labelKey: 'events.formFieldsMenu.longText',
            description: t('events.formFieldsMenu.longTextDescription'),
            type: 'multiline',
            icon: _jsx(TextBlock, { variant: 'Outline', size: 20, color: theme.palette.common.black })
        },
        {
            id: 'radio',
            label: t('events.formFieldsMenu.choiceRadio'),
            labelKey: 'events.formFieldsMenu.choiceRadio',
            description: t('events.formFieldsMenu.choiceRadioDescription'),
            type: 'radio',
            icon: (_jsx(Icon, { icon: 'eva:radio-button-on-fill', fontSize: '20', color: theme.palette.common.black }))
        },
        {
            id: 'choiceButton',
            label: t('events.formFieldsMenu.choiceButton'),
            labelKey: 'events.formFieldsMenu.choiceButton',
            description: t('events.formFieldsMenu.choiceButtonDescription'),
            type: 'choice',
            icon: (_jsx(Icon, { icon: 'eva:radio-button-on-outline', fontSize: '20', color: theme.palette.common.black }))
        },
        {
            id: 'multipleChoice',
            label: t('events.formFieldsMenu.multipleChoiceCheckbox'),
            labelKey: 'events.formFieldsMenu.multipleChoiceCheckbox',
            description: t('events.formFieldsMenu.multipleChoiceCheckboxDescription'),
            type: 'checkbox-group',
            icon: _jsx(TickSquare, { variant: 'Outline', size: 20, color: theme.palette.common.black })
        },
        {
            id: 'additionalAttendees',
            label: t('events.formFieldsMenu.additionalAttendees'),
            labelKey: 'events.formFieldsMenu.additionalAttendees',
            description: t('events.formFieldsMenu.additionalAttendeesDescription'),
            type: ADDITIONAL_ATTENDEES_TYPE,
            icon: _jsx(Buildings, { variant: 'Outline', size: 20, color: theme.palette.common.black })
        },
        {
            id: 'dropdown',
            label: t('events.formFieldsMenu.dropdown'),
            labelKey: 'events.formFieldsMenu.dropdown',
            description: t('events.formFieldsMenu.dropdownDescription'),
            type: 'dropdown',
            icon: _jsx(DragHandleOutlinedIcon, { fontSize: 'small', sx: { color: theme.palette.common.black } })
        },
        {
            id: 'companyDropdown',
            label: t('events.formFieldsMenu.companyDropdown'),
            labelKey: 'events.formFieldsMenu.companyDropdown',
            description: t('events.formFieldsMenu.companyDropdownDescription'),
            type: 'companyDropdown',
            disabled: hasCompanyDropdownField,
            disabledTooltip: hasCompanyDropdownField
                ? getAddedOnceTooltip(t('events.formFieldsMenu.companyDropdown'))
                : undefined,
            icon: _jsx(Buildings, { variant: 'Outline', size: 20, color: theme.palette.common.black })
        },
        {
            id: 'multiSelectDropdown',
            label: t('events.formFieldsMenu.multiSelectDropdown'),
            labelKey: 'events.formFieldsMenu.multiSelectDropdown',
            description: t('events.formFieldsMenu.multiSelectDropdownDescription'),
            type: 'multi-select',
            icon: _jsx(Task, { variant: 'Outline', size: 20, color: theme.palette.common.black })
        },
        {
            id: 'toggle',
            label: t('events.formFieldsMenu.toggleYesNo'),
            labelKey: 'events.formFieldsMenu.toggleYesNo',
            description: t('events.formFieldsMenu.toggleYesNoDescription'),
            type: 'toggle',
            icon: _jsx(ToggleOnCircle, { variant: 'Outline', size: 20, color: theme.palette.common.black })
        },
        {
            id: 'undertaking',
            label: t('events.formFieldsMenu.undertaking'),
            labelKey: 'events.formFieldsMenu.undertaking',
            description: t('events.formFieldsMenu.undertakingDescription'),
            type: 'undertaking',
            disabled: hasUndertakingField,
            disabledTooltip: hasUndertakingField
                ? getAddedOnceTooltip(t('events.formFieldsMenu.undertaking'))
                : undefined,
            icon: (_jsx(Icon, { icon: 'material-symbols:description', fontSize: '20', color: theme.palette.common.black }))
        },
        {
            id: 'pickupLocation',
            label: t('events.formFieldsMenu.pickupLocation'),
            labelKey: 'events.formFieldsMenu.pickupLocation',
            description: t('events.formFieldsMenu.pickupLocationDescription'),
            type: 'pickupLocation',
            disabled: hasPickupLocationField,
            disabledTooltip: hasPickupLocationField
                ? getAddedOnceTooltip(t('events.formFieldsMenu.pickupLocation'))
                : undefined,
            icon: _jsx(Icon, { icon: 'eva:pin-fill', fontSize: '20', color: theme.palette.common.black })
        },
        {
            id: 'transportationPreset',
            label: t('events.formFieldsMenu.transportationPreset'),
            labelKey: 'events.formFieldsMenu.transportationPreset',
            description: t('events.formFieldsMenu.transportationPresetDescription'),
            type: 'transportationPreset',
            disabled: hasTransportationPresetField || hasStandalonePickupLocationField,
            disabledTooltip: hasStandalonePickupLocationField
                ? t('events.formFieldsMenu.transportationPresetCannotbeAddedWithPickupLocation')
                : hasTransportationPresetField
                    ? getAddedOnceTooltip(t('events.formFieldsMenu.transportationPreset'))
                    : undefined,
            icon: _jsx(Icon, { icon: 'mingcute:bus-2-line', fontSize: '20', color: theme.palette.common.black })
        }
    ], [
        t,
        theme.palette.common.black,
        hasEmailField,
        hasPhoneField,
        hasCompanyDropdownField,
        hasUndertakingField,
        hasPickupLocationField,
        hasTransportationPresetField,
        hasStandalonePickupLocationField,
        getAddedOnceTooltip
    ]);
    const restrictedMenuSchema = useMemo(() => {
        if (!restrictToTypes || restrictToTypes.length === 0)
            return menuSchema;
        return menuSchema.reduce((acc, item) => {
            if (item.children && item.children.length > 0) {
                const filteredChildren = item.children.filter((child) => !child.type || restrictToTypes.includes(child.type));
                if (filteredChildren.length > 0)
                    acc.push({ ...item, children: filteredChildren });
                return acc;
            }
            if (!item.type || restrictToTypes.includes(item.type))
                acc.push(item);
            return acc;
        }, []);
    }, [menuSchema, restrictToTypes]);
    const filteredItems = useMemo(() => {
        return restrictedMenuSchema.filter((item) => (item.labelKey ? t(item.labelKey) : item.label)
            .toLowerCase()
            .includes(searchTerm.toLowerCase()));
    }, [restrictedMenuSchema, searchTerm, t]);
    const standardFieldIds = useMemo(() => new Set(['singleLineInput', 'longText', 'radio', 'choiceButton', 'multipleChoice', 'toggle']), []);
    const standardItems = useMemo(() => filteredItems.filter((item) => standardFieldIds.has(item.id)), [filteredItems, standardFieldIds]);
    const advancedItems = useMemo(() => filteredItems.filter((item) => !standardFieldIds.has(item.id)), [filteredItems, standardFieldIds]);
    const parentItem = useMemo(() => {
        if (isMobile) {
            return filteredItems.find((item) => item.children &&
                submenuAnchor?.textContent?.includes(item.labelKey ? t(item.labelKey) : item.label));
        }
        else {
            const activeItemId = hoveredItem || clickedItem;
            return filteredItems.find((item) => item.id === activeItemId);
        }
    }, [isMobile, filteredItems, submenuAnchor?.textContent, t, hoveredItem, clickedItem]);
    const handleMouseEnter = (item, element) => {
        if (item.children && item.children.length > 0) {
            if (submenuTimeoutRef.current) {
                clearTimeout(submenuTimeoutRef.current);
            }
            setHoveredItem(item.id);
            setClickedItem(item.id);
            setSubmenuAnchor(element);
        }
    };
    const handleItemMouseEnter = (item, e) => {
        if (submenuTimeoutRef.current) {
            clearTimeout(submenuTimeoutRef.current);
        }
        if (clickedItem)
            return;
        if (item.children && item.children.length > 0) {
            handleMouseEnter(item, e.currentTarget);
        }
        else {
            setSubmenuAnchor(null);
            setHoveredItem(null);
            setClickedItem(null);
        }
    };
    const handleMouseLeave = () => {
        if (!isMobile) {
            submenuTimeoutRef.current = setTimeout(() => {
                if (!clickedItem) {
                    setSubmenuAnchor(null);
                    setHoveredItem(null);
                }
            });
        }
    };
    const handleSubmenuMouseEnter = () => {
        if (submenuTimeoutRef.current) {
            clearTimeout(submenuTimeoutRef.current);
        }
    };
    const handleSubmenuMouseLeave = () => {
        if (!isMobile) {
            if (!clickedItem) {
                setSubmenuAnchor(null);
                setHoveredItem(null);
            }
        }
    };
    const handleItemClick = (e, item) => {
        const hasChildren = item.children && item.children.length > 0;
        if (hasChildren) {
            if (item.disabled)
                return;
            if (isMobile) {
                setSubmenuAnchor(e.currentTarget);
                setHoveredItem(item.id);
                setClickedItem(item.id);
            }
            else {
                if (clickedItem === item.id) {
                    setClickedItem(null);
                    setSubmenuAnchor(null);
                    setHoveredItem(null);
                }
                else {
                    setClickedItem(item.id);
                    setHoveredItem(item.id);
                    setSubmenuAnchor(e.currentTarget);
                }
            }
        }
        else {
            if (item.disabled)
                return;
            onSelect?.({
                parent: item,
                type: item.type,
                child: undefined,
                description: item?.description || ''
            });
            onClose();
        }
    };
    const handleSubmenuClickAway = (event) => {
        const target = event.target;
        const isClickOnMainMenu = anchorRef?.contains(target);
        const isClickOnSubmenu = submenuAnchor?.contains(target);
        if (!isClickOnMainMenu && !isClickOnSubmenu) {
            setSubmenuAnchor(null);
            setHoveredItem(null);
            setClickedItem(null);
        }
    };
    useEffect(() => {
        return () => {
            if (submenuTimeoutRef.current) {
                clearTimeout(submenuTimeoutRef.current);
            }
        };
    }, []);
    useEffect(() => {
        if (!submenuAnchor || !scrollContainerRef.current || !stickyHeaderRef.current)
            return;
        const scrollEl = scrollContainerRef.current;
        const stickyEl = stickyHeaderRef.current;
        const checkAnchorVisibility = () => {
            const anchorRect = submenuAnchor.getBoundingClientRect();
            const stickyRect = stickyEl.getBoundingClientRect();
            if (anchorRect.bottom <= stickyRect.bottom + 2) {
                setSubmenuAnchor(null);
                setHoveredItem(null);
                setClickedItem(null);
            }
        };
        scrollEl.addEventListener('scroll', checkAnchorVisibility, { passive: true });
        return () => scrollEl.removeEventListener('scroll', checkAnchorVisibility);
    }, [submenuAnchor]);
    useLayoutEffect(() => {
        if (searchTerm.trim().length > 0 && advancedItems.length > 0) {
            setShowAdvancedOptions(true);
        }
    }, [searchTerm, advancedItems.length]);
    return (_jsxs(_Fragment, { children: [_jsxs(Menu, { anchorEl: anchorRef, onClose: onClose, anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right'
                }, open: open, slotProps: {
                    paper: {
                        sx: {
                            background: (theme) => theme.palette.background.paper,
                            boxShadow: (theme) => theme.customShadows.card,
                            maxWidth: isMobile ? 352 : 440,
                            borderRadius: 4
                        }
                    }
                }, children: [_jsxs(Box, { className: 'menu-header', sx: {
                            display: 'flex',
                            gap: 0.5,
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: '17px',
                            px: 2.5
                        }, children: [_jsx(Typography, { variant: 'h6', color: theme.palette.text.primary, children: t('events.formFieldsMenu.components') }), _jsx(IconButton, { size: 'small', color: 'default', onClick: () => onClose && onClose(), sx: { border: 'none' }, children: _jsx(Icon, { icon: 'eva:close-fill', color: theme.palette.text.secondary }) })] }), _jsxs(Box, { ref: scrollContainerRef, sx: {
                            display: 'flex',
                            flexDirection: 'column',
                            px: 2.5,
                            gap: 1,
                            maxHeight: 'calc(100dvh - 400px)',
                            overflowY: 'auto'
                        }, children: [_jsx(Box, { ref: stickyHeaderRef, sx: {
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 1,
                                    pb: 1,
                                    backgroundColor: (theme) => theme.palette.background.paper
                                }, children: _jsx(HFilledTextField, { size: 'medium', placeholder: t('placeholders.search'), onChange: (e) => setSearchTerm(e.target.value), onKeyDown: (e) => e.stopPropagation(), slotProps: {
                                        input: {
                                            sx: {
                                                background: alpha(theme.palette.grey[500], 0.08),
                                                color: theme.palette.text.secondary,
                                                '::placeholder': {
                                                    color: theme.palette.text.secondary
                                                },
                                                input: {
                                                    py: '15.5px',
                                                    pl: 1
                                                }
                                            },
                                            startAdornment: (_jsx(SearchNormal1, { variant: 'Outline', fontSize: '24', color: theme.palette.text.disabled }))
                                        }
                                    } }) }), _jsxs(MenuList, { sx: { minWidth: 310 }, children: [_jsx(Typography, { variant: 'body1', sx: { px: 1, py: 0.5, color: (theme) => theme.palette.text.primary }, children: t('events.formFieldsMenu.standard') }), standardItems.map((item) => {
                                        const isParentSelected = item.id === selectedItemId;
                                        const hasChildren = item.children && item.children.length > 0;
                                        const isActive = hoveredItem === item.id || clickedItem === item.id;
                                        const isDisabled = !!item.disabled;
                                        return (_jsxs(MuiMenuItem, { "aria-disabled": isDisabled, onClick: (e) => handleItemClick(e, item), onMouseEnter: !isMobile
                                                ? (e) => handleItemMouseEnter(item, e)
                                                : undefined, onMouseLeave: !isMobile ? handleMouseLeave : undefined, sx: {
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                py: '7px',
                                                px: '8px',
                                                gap: 1,
                                                borderRadius: 1,
                                                position: 'relative',
                                                color: (theme) => theme.palette.text.primary,
                                                opacity: isDisabled ? 0.6 : 1,
                                                cursor: isDisabled ? 'default' : 'pointer',
                                                background: (theme) => isParentSelected || isActive ? theme.palette.action.hover : '',
                                                ':hover': {
                                                    backgroundColor: (theme) => theme.palette.action.hover
                                                }
                                            }, children: [_jsx(ListItemIcon, { sx: {
                                                        backgroundColor: theme.palette.background.neutral,
                                                        borderRadius: 1,
                                                        padding: 1,
                                                        minHeight: '32px',
                                                        minWidth: '32px'
                                                    }, children: item.icon }), _jsx(ListItemText, { primary: item.labelKey ? t(item.labelKey) : item.label, secondary: item.description, slotProps: {
                                                        primary: {
                                                            sx: {
                                                                ...theme.typography.body2
                                                            }
                                                        },
                                                        secondary: {
                                                            sx: {
                                                                ...theme.typography.caption,
                                                                color: theme.palette.text.secondary,
                                                                whiteSpace: 'break-spaces'
                                                            }
                                                        }
                                                    } }), isDisabled && item.disabledTooltip && (_jsx(Tooltip, { title: item.disabledTooltip, placement: 'top', children: _jsx(ListItemIcon, { sx: { minWidth: 'unset', ml: 0.5 }, children: _jsx(Icon, { icon: 'eva:info-outline', fontSize: '16', color: theme.palette.text.secondary }) }) })), hasChildren && (_jsx(ListItemIcon, { sx: { position: 'absolute', right: 8, minWidth: 'unset' }, children: _jsx(Icon, { icon: 'eva:arrow-ios-forward-fill', fontSize: '20', color: theme.palette.text.primary }) }))] }, item.id));
                                    }), _jsxs(Box, { sx: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            mt: 0.5,
                                            px: 1,
                                            py: 0.5
                                        }, children: [_jsx(Typography, { variant: 'body1', sx: { color: (theme) => theme.palette.text.primary }, children: t('events.formFieldsMenu.advanced') }), _jsx(Typography, { fontSize: 13, onClick: () => setShowAdvancedOptions((prev) => !prev), sx: {
                                                    color: (theme) => theme.palette.primary.main,
                                                    fontWeight: 500,
                                                    cursor: 'pointer',
                                                    userSelect: 'none'
                                                }, children: showAdvancedOptions
                                                    ? t('events.formFieldsMenu.hideOptions')
                                                    : t('events.formFieldsMenu.showOptions') })] }), showAdvancedOptions &&
                                        advancedItems.map((item) => {
                                            const isParentSelected = item.id === selectedItemId;
                                            const hasChildren = item.children && item.children.length > 0;
                                            const isActive = hoveredItem === item.id || clickedItem === item.id;
                                            const isDisabled = !!item.disabled;
                                            return (_jsxs(MuiMenuItem, { "aria-disabled": isDisabled, onClick: (e) => handleItemClick(e, item), onMouseEnter: !isMobile
                                                    ? (e) => handleItemMouseEnter(item, e)
                                                    : undefined, onMouseLeave: !isMobile ? handleMouseLeave : undefined, sx: {
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    py: '7px',
                                                    px: '8px',
                                                    gap: 1,
                                                    borderRadius: 1,
                                                    position: 'relative',
                                                    color: (theme) => theme.palette.text.primary,
                                                    opacity: isDisabled ? 0.6 : 1,
                                                    cursor: isDisabled ? 'default' : 'pointer',
                                                    background: (theme) => isParentSelected || isActive ? theme.palette.action.hover : '',
                                                    ':hover': {
                                                        backgroundColor: (theme) => theme.palette.action.hover
                                                    }
                                                }, children: [_jsx(ListItemIcon, { sx: {
                                                            backgroundColor: theme.palette.background.neutral,
                                                            borderRadius: 1,
                                                            padding: 1,
                                                            minHeight: '32px',
                                                            minWidth: '32px'
                                                        }, children: item.icon }), _jsx(ListItemText, { primary: item.labelKey ? t(item.labelKey) : item.label, secondary: item.description, slotProps: {
                                                            primary: {
                                                                sx: {
                                                                    ...theme.typography.body2
                                                                }
                                                            },
                                                            secondary: {
                                                                sx: {
                                                                    ...theme.typography.caption,
                                                                    color: theme.palette.text.secondary,
                                                                    whiteSpace: 'break-spaces'
                                                                }
                                                            }
                                                        } }), isDisabled && item.disabledTooltip && (_jsx(Tooltip, { title: item.disabledTooltip, placement: 'top', children: _jsx(ListItemIcon, { sx: { minWidth: 'unset', ml: 0.5 }, children: _jsx(Icon, { icon: 'eva:info-outline', fontSize: '16', color: theme.palette.text.secondary }) }) })), hasChildren && (_jsx(ListItemIcon, { sx: { position: 'absolute', right: 8, minWidth: 'unset' }, children: _jsx(Icon, { icon: 'eva:arrow-ios-forward-fill', fontSize: '20', color: theme.palette.text.primary }) }))] }, item.id));
                                        })] })] })] }), _jsx(Popper, { open: !!submenuAnchor, anchorEl: submenuAnchor, placement: isMobile ? 'auto' : 'right-start', style: { zIndex: 1300 }, sx: {
                    '&[data-popper-placement*="left"] .submenu-paper': {
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0
                    },
                    '&[data-popper-placement*="right"] .submenu-paper': {
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0
                    }
                }, children: _jsx(ClickAwayListener, { onClickAway: handleSubmenuClickAway, children: _jsx(Paper, { className: 'submenu-paper', sx: {
                            background: (theme) => theme.palette.common.white,
                            minWidth: 176,
                            borderRadius: 4,
                            py: '7px',
                            px: '8px'
                        }, onMouseEnter: !isMobile ? handleSubmenuMouseEnter : undefined, onMouseLeave: !isMobile ? handleSubmenuMouseLeave : undefined, children: _jsx(MenuList, { children: parentItem?.children?.map((child) => {
                                const parentItemDetails = filteredItems.find((item) => clickedItem === item.id); // get the item details by clicked itemId and extracting description to be used as description for tooltip
                                const descriptionForTooltip = parentItemDetails?.description || '';
                                const isSubItemSelected = child.id === selectedItemId;
                                const isSubItemDisabled = !!child.disabled;
                                return (_jsxs(MuiMenuItem, { "aria-disabled": isSubItemDisabled, onClick: () => {
                                        if (isSubItemDisabled)
                                            return;
                                        onSelect?.({
                                            parent: parentItem,
                                            child: child,
                                            type: child.type,
                                            description: descriptionForTooltip || ''
                                        });
                                        setSubmenuAnchor(null);
                                        setHoveredItem(null);
                                        setClickedItem(null);
                                        onClose();
                                    }, sx: {
                                        color: (theme) => theme.palette.text.primary,
                                        background: (theme) => (isSubItemSelected ? theme.palette.action.hover : ''),
                                        opacity: isSubItemDisabled ? 0.6 : 1,
                                        cursor: isSubItemDisabled ? 'default' : 'pointer',
                                        ':hover': {
                                            backgroundColor: (theme) => theme.palette.action.hover
                                        }
                                    }, children: [_jsx(ListItemText, { primary: child.labelKey ? t(child.labelKey) : child.label, slotProps: {
                                                primary: {
                                                    sx: {
                                                        ...theme.typography.body2
                                                    }
                                                }
                                            } }), isSubItemDisabled && child.disabledTooltip && (_jsx(Tooltip, { title: child.disabledTooltip, placement: 'top', children: _jsx(ListItemIcon, { sx: { minWidth: 'unset', ml: 0.5 }, children: _jsx(Icon, { icon: 'eva:info-outline', fontSize: '16', color: theme.palette.text.secondary }) }) }))] }, child.id));
                            }) }) }) }) })] }));
};
export default FormFieldsMenu;
